import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
    FaStar,
    FaWhatsapp,
    FaMapMarkerAlt,
    FaTag,
    FaBoxOpen,
    FaTruck,
    FaShieldAlt,
    FaPhone,
    FaChevronRight,
    FaArrowLeft,
    FaHeart,
} from "react-icons/fa";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    useAddsTodoMutation,
    useGetsTodosQuery,
    useDeleteeTodoMutation,
} from "../../redux/slices/todoApiSlice";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";

const DetalPaginat = () => {
    const { pagination_id } = useParams();
    const navigate = useNavigate();

    // =========================================================
    // USER
    // =========================================================

    const userInfo = useSelector(
        (state) => state.auth?.userInfo
    );

    // =========================================================
    // FAVORITE RTK QUERY
    // =========================================================

    const {
        data: favoriteData,
    } = useGetsTodosQuery(undefined, {
        skip: !userInfo?._id,
    });

    const [addFavorite] = useAddsTodoMutation();
    const [deleteFavorite] = useDeleteeTodoMutation();

    // =========================================================
    // PRODUCT STATE
    // =========================================================

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [thumbsSwiper, setThumbsSwiper] = useState(null);

    const [visibleDescription, setVisibleDescription] = useState(300);
    const [visibleDistance, setVisibleDistance] = useState(150);

    const [relatedProducts, setRelatedProducts] = useState([]);

    // Əsas məhsulun favorite loading-i
    const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);

    // Oxşar məhsullardan hansı favorite əməliyyatındadır
    const [favoriteLoadingId, setFavoriteLoadingId] = useState(null);

    // =========================================================
    // FAVORITE DATA
    // =========================================================

    const favoriteItems =
        favoriteData?.todos ||
        favoriteData?.allTodos ||
        favoriteData?.favorites ||
        favoriteData ||
        [];

    // =========================================================
    // FAVORITE PRODUCT ID HELPER
    // =========================================================

    const getFavoriteProductId = (item) => {
        if (!item) return null;

        if (
            typeof item?.productId === "object" &&
            item?.productId?._id
        ) {
            return item.productId._id;
        }

        if (item?.productId) {
            return item.productId;
        }

        if (
            typeof item?.product === "object" &&
            item?.product?._id
        ) {
            return item.product._id;
        }

        if (item?.product) {
            return item.product;
        }

        return null;
    };

    // =========================================================
    // FIND FAVORITE ITEM BY PRODUCT ID
    // =========================================================

    const getFavoriteItem = (productId) => {
        if (!Array.isArray(favoriteItems) || !productId) {
            return null;
        }

        return (
            favoriteItems.find((item) => {
                const favoriteProductId =
                    getFavoriteProductId(item);

                return (
                    String(favoriteProductId || "") ===
                    String(productId || "")
                );
            }) || null
        );
    };

    // =========================================================
    // CURRENT PRODUCT FAVORITE
    // =========================================================

    const favoriteItem = getFavoriteItem(
        product?._id
    );

    const isFavorite = Boolean(favoriteItem);

    // =========================================================
    // SƏHİFƏ YÜKLƏNƏNDƏ ƏN YUXARIYA ATIN
    // =========================================================

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pagination_id]);

    // =========================================================
    // MƏHSULU GƏTİR
    // =========================================================

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                setError(null);

                const url = `${process.env.REACT_APP_API_BASE_URL}/api/pagination/id/${pagination_id}`;

                const response = await axios.get(url);

                const data = response.data;

                if (data?.getById) {
                    setProduct(data.getById);

                    setRelatedProducts(
                        Array.isArray(data.relatedProducts)
                            ? data.relatedProducts
                            : []
                    );
                } else {
                    setProduct(null);
                    setRelatedProducts([]);
                    setError("Elan tapılmadı.");
                }
            } catch (error) {
                console.error(
                    "❌ Məhsul alınarkən xəta:",
                    error?.response?.data || error
                );

                setProduct(null);
                setRelatedProducts([]);

                if (error?.response?.status === 404) {
                    setError(
                        "Bu elan artıq mövcud deyil."
                    );
                } else {
                    setError(
                        error?.response?.data?.message ||
                        "Elan detalları alınarkən xəta baş verdi."
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        if (pagination_id) {
            fetchProduct();
        }
    }, [pagination_id]);

    // =========================================================
    // ƏSAS ELAN - FAVORİYƏ ƏLAVƏ ET / SİL
    // =========================================================

    const handleFavorite = async () => {
        if (!userInfo?._id) {
            toast.info(
                "Sevimlilərə əlavə etmək üçün əvvəlcə daxil olun."
            );
            return;
        }

        if (!product?._id) {
            toast.error(
                "Elanın identifikasiya məlumatı tapılmadı."
            );
            return;
        }

        if (isFavoriteLoading) {
            return;
        }

        try {
            setIsFavoriteLoading(true);

            // =================================================
            // FAVORİDƏDİRSƏ -> SİL
            // =================================================

            if (isFavorite) {
                if (!favoriteItem?._id) {
                    toast.error(
                        "Sevimli elan məlumatı tapılmadı."
                    );
                    return;
                }

                await deleteFavorite(
                    favoriteItem._id
                ).unwrap();

                toast.success(
                    "Elan sevimlilərdən uğurla silindi."
                );

                return;
            }

            // =================================================
            // FAVORİDƏ DEYİLSƏ -> ƏLAVƏ ET
            // =================================================

            await addFavorite({
                productId: product._id,
            }).unwrap();

            toast.success(
                "Elan sevimlilərə uğurla əlavə edildi."
            );
        } catch (error) {
            console.error(
                "❌ Sevimli əməliyyatı xətası:",
                error
            );

            const message =
                error?.data?.message ||
                error?.data?.error ||
                error?.error ||
                "Sevimli əməliyyatı zamanı xəta baş verdi.";

            toast.error(message);
        } finally {
            setIsFavoriteLoading(false);
        }
    };

    // =========================================================
    // OXŞAR ELAN - FAVORİYƏ ƏLAVƏ ET / SİL
    // =========================================================

    const handleRelatedFavorite = async (
        e,
        relatedProduct
    ) => {
        // Kartın öz onClick-i işləməsin
        e.preventDefault();
        e.stopPropagation();

        if (!userInfo?._id) {
            toast.info(
                "Sevimlilərə əlavə etmək üçün əvvəlcə daxil olun."
            );
            return;
        }

        if (!relatedProduct?._id) {
            toast.error(
                "Elanın identifikasiya məlumatı tapılmadı."
            );
            return;
        }

        const relatedProductId =
            String(relatedProduct._id);

        if (
            favoriteLoadingId === relatedProductId
        ) {
            return;
        }

        const relatedFavorite =
            getFavoriteItem(
                relatedProduct._id
            );

        try {
            setFavoriteLoadingId(
                relatedProductId
            );

            // =================================================
            // ARTİQ FAVORİDƏDİRSƏ -> SİL
            // =================================================

            if (relatedFavorite) {
                if (!relatedFavorite?._id) {
                    toast.error(
                        "Sevimli elan məlumatı tapılmadı."
                    );
                    return;
                }

                await deleteFavorite(
                    relatedFavorite._id
                ).unwrap();

                toast.success(
                    "Elan sevimlilərdən uğurla silindi."
                );

                return;
            }

            // =================================================
            // FAVORİDƏ DEYİLSƏ -> ƏLAVƏ ET
            // =================================================

            await addFavorite({
                productId:
                    relatedProduct._id,
            }).unwrap();

            toast.success(
                "Elan sevimlilərə uğurla əlavə edildi."
            );
        } catch (error) {
            console.error(
                "❌ Oxşar elan favorite xətası:",
                error
            );

            const message =
                error?.data?.message ||
                error?.data?.error ||
                error?.error ||
                "Sevimli əməliyyatı zamanı xəta baş verdi.";

            toast.error(message);
        } finally {
            setFavoriteLoadingId(null);
        }
    };

    // =========================================================
    // YÜKLƏNİR
    // =========================================================

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center px-4">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />

                    <p className="text-gray-500 dark:text-gray-400">
                        Elan yüklənir...
                    </p>
                </div>
            </div>
        );
    }

    // =========================================================
    // ERROR
    // =========================================================

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="text-6xl mb-5">
                        😕
                    </div>

                    <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-white">
                        {error}
                    </h2>

                    <button
                        onClick={() => navigate(-1)}
                        className="mt-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition"
                    >
                        <FaArrowLeft />
                        Geri qayıt
                    </button>
                </div>
            </div>
        );
    }

    // =========================================================
    // PRODUCT YOXDUR
    // =========================================================

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
                <p className="text-gray-600 dark:text-gray-300">
                    Elan tapılmadı.
                </p>
            </div>
        );
    }

    // =========================================================
    // DATA
    // =========================================================

    const photos =
        Array.isArray(product.photo) &&
            product.photo.length > 0
            ? product.photo
            : [];

    const description =
        product.description || "";

    const distance =
        product.distance || "";

    const cleanPhone =
        (product.phone || "").replace(/\D/g, "");

    const whatsappPhone =
        cleanPhone.startsWith("994")
            ? cleanPhone
            : `994${cleanPhone.replace(/^0/, "")}`;

    // =========================================================
    // DATE
    // =========================================================

const formattedDate = product.createdAt
    ? (() => {
        const date = new Date(product.createdAt);

        const months = [
            "yanvar",
            "fevral",
            "mart",
            "aprel",
            "may",
            "iyun",
            "iyul",
            "avqust",
            "sentyabr",
            "oktyabr",
            "noyabr",
            "dekabr",
        ];

        const day = String(date.getDate()).padStart(2, "0");
        const month = months[date.getMonth()];
        const year = date.getFullYear();

        return `${day} ${month} ${year}`;
    })()
    : "";

    // =========================================================
    // CONDITION
    // =========================================================

    const conditionClass = {
        "Yeni":
            "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",

        "Əla vəziyyətdə":
            "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",

        "Yaxşı vəziyyətdə":
            "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",

        "İşlənmiş":
            "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-gray-300",

        "Təmirə ehtiyacı var":
            "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };

    // =========================================================
    // DELIVERY
    // =========================================================

    const deliveryText =
        product.delivery || "Satıcıdan götürmə";

    // =========================================================
    // HOME - BÜTÜN ELANLARA KEÇİD
    // =========================================================

    const goToAllProducts = () => {
        navigate("/");

        setTimeout(() => {
            const section = document.getElementById(
                "all-productsssssssssssss"
            );

            if (section) {
                section.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }, 250);
    };

    // =========================================================
    // RELATED PRODUCT
    // =========================================================

    const openRelatedProduct = (item) => {
        const itemId =
            item.pagination_id ||
            item._id ||
            item.id;

        if (!itemId) return;

        navigate(`/pagination/${itemId}`);
    };

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-800 dark:text-white">

            {/* =====================================================
                MAIN CONTAINER
            ===================================================== */}

            <div className="container mx-auto px-3 sm:px-5 lg:px-8 xl:px-12 2xl:px-16 py-5 sm:py-7 lg:py-10">

                {/* =====================================================
                    BACK
                ===================================================== */}

                <button
                    onClick={() => navigate(-1)}
                    className="mb-5 sm:mb-6 inline-flex items-center gap-2 text-sm sm:text-base text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition"
                >
                    <FaArrowLeft />
                    Geri
                </button>

                {/* =====================================================
                    BREADCRUMB
                ===================================================== */}

                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-5 sm:mb-6 overflow-hidden">

                    <span className="shrink-0">
                        Elanlar
                    </span>

                    <FaChevronRight className="text-[9px] shrink-0" />

                    <span className="shrink-0 truncate max-w-[120px] sm:max-w-none">
                        {product.category}
                    </span>

                    <FaChevronRight className="text-[9px] shrink-0" />

                    <span className="truncate">
                        {product.title}
                    </span>

                </div>

                {/* =====================================================
                    PRODUCT GRID
                ===================================================== */}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 sm:gap-9 lg:gap-10 xl:gap-14">

                    {/* =================================================
                        LEFT - PHOTOS
                    ================================================= */}

                    <div className="w-full min-w-0">

                        <div className="bg-white dark:bg-zinc-950 rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm border border-gray-200 dark:border-zinc-800">

                            {photos.length > 0 ? (
                                <Swiper
                                    spaceBetween={10}
                                    navigation={photos.length > 1}
                                    thumbs={{
                                        swiper:
                                            thumbsSwiper &&
                                                !thumbsSwiper.destroyed
                                                ? thumbsSwiper
                                                : null,
                                    }}
                                    modules={[
                                        Navigation,
                                        Thumbs,
                                    ]}
                                    className="w-full"
                                >
                                    {photos.map(
                                        (photo, index) => (
                                            <SwiperSlide
                                                key={`${photo}-${index}`}
                                            >
                                                <div className="w-full h-[280px] xs:h-[320px] sm:h-[400px] lg:h-[500px] xl:h-[540px] bg-gray-100 dark:bg-zinc-900">

                                                    <img
                                                        src={photo}
                                                        alt={`${product.title} - ${index + 1}`}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display =
                                                                "none";
                                                        }}
                                                    />

                                                </div>
                                            </SwiperSlide>
                                        )
                                    )}
                                </Swiper>
                            ) : (
                                <div className="w-full h-[280px] sm:h-[400px] lg:h-[500px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">

                                    <div className="text-center text-gray-400">

                                        <FaBoxOpen className="text-5xl mx-auto mb-3" />

                                        <p>
                                            Şəkil yoxdur
                                        </p>

                                    </div>

                                </div>
                            )}

                        </div>

                        {/* =================================================
                            THUMBNAILS
                        ================================================= */}

                        {photos.length > 1 && (
                            <Swiper
                                onSwiper={setThumbsSwiper}
                                spaceBetween={8}
                                slidesPerView={Math.min(
                                    5,
                                    photos.length
                                )}
                                watchSlidesProgress
                                modules={[Thumbs]}
                                className="mt-3 sm:mt-4"
                            >
                                {photos.map(
                                    (photo, index) => (
                                        <SwiperSlide
                                            key={`thumb-${photo}-${index}`}
                                            className="cursor-pointer"
                                        >
                                            <div className="h-14 sm:h-20 rounded-lg sm:rounded-xl overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all">

                                                <img
                                                    src={photo}
                                                    alt={`thumbnail-${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />

                                            </div>
                                        </SwiperSlide>
                                    )
                                )}
                            </Swiper>
                        )}

                    </div>

                    {/* =================================================
                        RIGHT - PRODUCT INFO
                    ================================================= */}

                    <div className="flex flex-col min-w-0">

                        {/* =================================================
                            TITLE
                        ================================================= */}

                        <div className="mb-4 sm:mb-5">

                            <div className="flex flex-wrap gap-2 mb-3">

                                {product.category && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 text-xs sm:text-sm font-medium">

                                        <FaTag />

                                        {product.category}

                                    </span>
                                )}

                                {product.condition && (
                                    <span
                                        className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium ${conditionClass[
                                            product.condition
                                        ] ||
                                            "bg-gray-100 text-gray-700"
                                            }`}
                                    >
                                        {product.condition}
                                    </span>
                                )}

                            </div>

                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight break-words">
                                {product.title}
                            </h1>

                        </div>

                        {/* =================================================
                            PRICE
                        ================================================= */}

                        <div className="mb-5">

                            <div className="flex flex-wrap items-center gap-3">

                                <span className="text-3xl sm:text-4xl font-bold text-blue-600 dark:text-blue-400">

                                    {Number(
                                        product.price || 0
                                    ).toLocaleString("az-AZ")}{" "}

                                    ₼

                                </span>

                                {product.negotiable && (
                                    <span className="px-3 py-1.5 rounded-lg bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs sm:text-sm font-semibold">
                                        Razılaşma mümkündür
                                    </span>
                                )}

                            </div>

                        </div>

                        {/* =================================================
                            RATING
                        ================================================= */}

                        <div className="flex items-center gap-1 mb-6">

                            {[...Array(5)].map(
                                (_, index) => (
                                    <FaStar
                                        key={index}
                                        className="text-yellow-400"
                                    />
                                )
                            )}

                            <span className="text-gray-400 text-sm ml-2">
                                (4.0)
                            </span>

                        </div>

                        <hr className="border-gray-200 dark:border-zinc-800 mb-6" />

                        {/* =================================================
                            QUICK INFO
                        ================================================= */}

                        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-7">

                            <div className="p-3 sm:p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                <p className="text-[11px] sm:text-xs text-gray-400 mb-1">
                                    Vəziyyət
                                </p>

                                <p className="font-semibold text-sm sm:text-base truncate">
                                    {product.condition || "—"}
                                </p>

                            </div>

                            <div className="p-3 sm:p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                <p className="text-[11px] sm:text-xs text-gray-400 mb-1">
                                    Brend
                                </p>

                                <p className="font-semibold text-sm sm:text-base truncate">
                                    {product.brand || "—"}
                                </p>

                            </div>

                            <div className="p-3 sm:p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                <p className="text-[11px] sm:text-xs text-gray-400 mb-1">
                                    Model
                                </p>

                                <p className="font-semibold text-sm sm:text-base truncate">
                                    {product.model || "—"}
                                </p>

                            </div>

                            <div className="p-3 sm:p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                <p className="text-[11px] sm:text-xs text-gray-400 mb-1">
                                    Stok
                                </p>

                                <p className="font-semibold text-sm sm:text-base">
                                    {product.stock || 1} ədəd
                                </p>

                            </div>

                        </div>

                        {/* =================================================
                            LOCATION
                        ================================================= */}

                        {(product.location ||
                            product.distance) && (
                                <div className="mb-5 sm:mb-6 p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                    <div className="flex items-start gap-3">

                                        <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-500 shrink-0">
                                            <FaMapMarkerAlt />
                                        </div>

                                        <div className="min-w-0">

                                            <p className="text-sm text-gray-400 mb-1">
                                                Məkan
                                            </p>

                                            {product.location && (
                                                <p className="font-semibold">
                                                    {product.location}
                                                </p>
                                            )}

                                            {product.distance && (
                                                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">

                                                    {distance.length >
                                                        visibleDistance
                                                        ? `${distance.slice(
                                                            0,
                                                            visibleDistance
                                                        )}...`
                                                        : distance}

                                                </div>
                                            )}

                                            {distance.length > 150 && (
                                                <button
                                                    onClick={() =>
                                                        setVisibleDistance(
                                                            visibleDistance >=
                                                                distance.length
                                                                ? 150
                                                                : distance.length
                                                        )
                                                    }
                                                    className="mt-1 text-sm text-blue-600 hover:underline"
                                                >
                                                    {visibleDistance <
                                                        distance.length
                                                        ? "Daha çox göstər"
                                                        : "Daha az göstər"}
                                                </button>
                                            )}

                                        </div>

                                    </div>

                                </div>
                            )}

                        {/* =================================================
                            DELIVERY
                        ================================================= */}

                        <div className="mb-5 sm:mb-6 p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                            <div className="flex items-center gap-3">

                                <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600">
                                    <FaTruck />
                                </div>

                                <div>

                                    <p className="text-sm text-gray-400">
                                        Çatdırılma
                                    </p>

                                    <p className="font-semibold">
                                        {deliveryText}
                                    </p>

                                </div>

                            </div>

                        </div>

                        {/* =================================================
                            WARRANTY
                        ================================================= */}

                        {product.warranty && (
                            <div className="mb-5 sm:mb-6 p-4 rounded-xl bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800">

                                <div className="flex items-center gap-3">

                                    <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600">
                                        <FaShieldAlt />
                                    </div>

                                    <div>

                                        <p className="text-sm text-gray-400">
                                            Zəmanət
                                        </p>

                                        <p className="font-semibold">
                                            {product.warranty}
                                        </p>

                                    </div>

                                </div>

                            </div>
                        )}

                        {/* =================================================
                            DESCRIPTION
                        ================================================= */}

                        <div className="mb-7">

                            <h2 className="text-xl font-bold mb-3">
                                Məhsul haqqında
                            </h2>

                            <div className="text-gray-600 dark:text-gray-300 leading-7 whitespace-pre-line break-words">

                                {description.length >
                                    visibleDescription
                                    ? `${description.slice(
                                        0,
                                        visibleDescription
                                    )}...`
                                    : description ||
                                    "Açıqlama əlavə edilməyib."}

                            </div>

                            {description.length > 300 && (
                                <button
                                    onClick={() =>
                                        setVisibleDescription(
                                            visibleDescription >=
                                                description.length
                                                ? 300
                                                : description.length
                                        )
                                    }
                                    className="mt-2 text-blue-600 hover:underline text-sm font-semibold"
                                >
                                    {visibleDescription <
                                        description.length
                                        ? "Daha çox göstər"
                                        : "Daha az göstər"}
                                </button>
                            )}

                        </div>

                        {/* =================================================
                            DATE
                        ================================================= */}

                        {formattedDate && (
                            <div className="text-sm text-gray-400 mb-5">
                                Elan tarixi: {formattedDate}
                            </div>
                        )}

                        {/* =================================================
                            MAIN FAVORITE
                        ================================================= */}

                        <div className="mb-5 sm:mb-6">

                            <button
                                type="button"
                                onClick={handleFavorite}
                                disabled={isFavoriteLoading}
                                className={`
                                    group
                                    relative
                                    w-full
                                    inline-flex
                                    items-center
                                    justify-center
                                    gap-3
                                    px-5
                                    py-4
                                    rounded-2xl
                                    font-bold
                                    transition-all
                                    duration-300
                                    border
                                    overflow-hidden
                                    disabled:opacity-60
                                    disabled:cursor-wait

                                    ${isFavorite
                                        ? "bg-gradient-to-r from-red-50 to-rose-50 text-red-600 border-red-200 hover:border-red-300 hover:shadow-lg hover:shadow-red-100 dark:from-red-950/30 dark:to-rose-950/20 dark:text-red-400 dark:border-red-900/60"
                                        : "bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600 hover:shadow-lg hover:shadow-red-100 dark:bg-zinc-950 dark:text-gray-200 dark:border-zinc-800 dark:hover:border-red-800 dark:hover:text-red-400"
                                    }
                                `}
                            >

                                <span
                                    className={`
                                        flex
                                        items-center
                                        justify-center
                                        w-10
                                        h-10
                                        rounded-full
                                        transition-all
                                        duration-300
                                        ${isFavorite
                                            ? "bg-red-100 text-red-500 dark:bg-red-900/40 dark:text-red-400"
                                            : "bg-gray-100 text-gray-400 group-hover:bg-red-50 group-hover:text-red-500 dark:bg-zinc-900 dark:text-gray-500 dark:group-hover:bg-red-950/40 dark:group-hover:text-red-400"
                                        }
                                    `}
                                >
                                    <FaHeart
                                        className={`
                                            text-lg
                                            transition-transform
                                            duration-300
                                            ${isFavorite
                                                ? "scale-110"
                                                : "group-hover:scale-110"
                                            }
                                        `}
                                    />
                                </span>

                                <span className="text-sm sm:text-base">
                                    {isFavorite
                                        ? "Sevimlilərinizdədir — silmək üçün basın"
                                        : "Sevimlilərə əlavə et"}
                                </span>

                            </button>

                        </div>

                        {/* =================================================
                            CONTACT
                        ================================================= */}

                        <div className="mt-auto">

                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                Satıcı ilə əlaqə:
                            </p>

                            {product.phone ? (
                                <div className="flex flex-col sm:flex-row gap-3">

                                    {/* WHATSAPP */}

                                    <a
                                        href={`https://api.whatsapp.com/send?phone=${whatsappPhone}&text=${encodeURIComponent(
                                            `Salam, "${product.title}" elanınızla maraqlanıram.`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white px-5 sm:px-6 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl"
                                    >

                                        <FaWhatsapp className="text-2xl" />

                                        WhatsApp ilə əlaqə

                                    </a>

                                    {/* PHONE */}

                                    <a
                                        href={`tel:${product.phone}`}
                                        className="sm:w-auto inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-bold transition"
                                    >

                                        <FaPhone />

                                        <span className="sm:hidden">
                                            Zəng et
                                        </span>

                                        <span className="hidden sm:inline">
                                            {product.phone}
                                        </span>

                                    </a>

                                </div>
                            ) : (
                                <div className="p-4 rounded-xl bg-gray-100 dark:bg-zinc-900 text-gray-500">
                                    Satıcının telefon nömrəsi əlavə edilməyib.
                                </div>
                            )}

                        </div>

                    </div>
                </div>

                {/* =========================================================
                    RELATED PRODUCTS
                ========================================================= */}

                {relatedProducts.length > 0 && (
                    <section className="mt-14 sm:mt-16 lg:mt-20 pt-8 sm:pt-10 border-t border-gray-200 dark:border-zinc-800">

                        {/* =================================================
                            SECTION HEADER
                        ================================================= */}

                        <div className="flex items-start justify-between gap-4 mb-6 sm:mb-8">

                            <div className="min-w-0">

                                <div className="flex items-center gap-2 mb-2">

                                    <span className="w-7 sm:w-9 h-1 rounded-full bg-blue-600"></span>

                                    <span className="text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                        {product.category}
                                    </span>

                                </div>

                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                                    Oxşar elanlar
                                </h2>

                                <p className="mt-1.5 sm:mt-2 text-xs sm:text-base text-gray-500 dark:text-gray-400">
                                    Elanın kateqoriyasına uyğun 8 təsadüfi elan göstərilir. Bütün elanları görmək üçün "Hamısına bax" düyməsinə toxunun.
                                </p>

                            </div>

                            <button
                                type="button"
                                onClick={goToAllProducts}
                                className="self-start shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-200 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:shadow-md transition-all duration-200 mt-1"
                            >

                                <span className="whitespace-nowrap">
                                    Hamısına bax
                                </span>

                                <FaChevronRight className="text-[10px] sm:text-xs" />

                            </button>

                        </div>

                        {/* =================================================
                            PRODUCTS GRID
                        ================================================= */}

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">

                            {relatedProducts.map(
                                (item) => {

                                    const image =
                                        Array.isArray(
                                            item.photo
                                        ) &&
                                            item.photo.length > 0
                                            ? item.photo[0]
                                            : typeof item.photo ===
                                                "string"
                                                ? item.photo
                                                : null;

                                    const itemId =
                                        item.pagination_id ||
                                        item._id ||
                                        item.id;

                                    const relatedFavorite =
                                        getFavoriteItem(
                                            item._id
                                        );

                                    const relatedIsFavorite =
                                        Boolean(
                                            relatedFavorite
                                        );

                                    const relatedIsLoading =
                                        favoriteLoadingId ===
                                        String(
                                            item?._id || ""
                                        );

                                    return (
                                        <article
                                            key={itemId}
                                            onClick={() =>
                                                openRelatedProduct(
                                                    item
                                                )
                                            }
                                            className="
                                                group
                                                relative
                                                cursor-pointer
                                                overflow-hidden
                                                rounded-2xl
                                                sm:rounded-3xl
                                                bg-white
                                                dark:bg-zinc-950
                                                border
                                                border-gray-200
                                                dark:border-zinc-800
                                                hover:border-blue-300
                                                dark:hover:border-blue-700
                                                hover:shadow-2xl
                                                hover:shadow-gray-200/60
                                                dark:hover:shadow-black/40
                                                hover:-translate-y-1
                                                transition-all
                                                duration-300
                                            "
                                        >

                                            {/* =================================================
                                                IMAGE
                                            ================================================= */}

                                            <div className="relative w-full aspect-square overflow-hidden bg-gray-100 dark:bg-zinc-900">

                                                {image ? (
                                                    <img
                                                        src={image}
                                                        alt={
                                                            item.title ||
                                                            "Məhsul"
                                                        }
                                                        loading="lazy"
                                                        className="
                                                            w-full
                                                            h-full
                                                            object-cover
                                                            group-hover:scale-105
                                                            transition-transform
                                                            duration-700
                                                        "
                                                        onError={(e) => {
                                                            e.currentTarget.style.display =
                                                                "none";
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-zinc-700">

                                                        <FaBoxOpen className="text-4xl sm:text-5xl" />

                                                    </div>
                                                )}

                                                {/* =================================================
                                                    PREMIUM IMAGE GRADIENT
                                                ================================================= */}

                                                <div className="
                                                    absolute
                                                    inset-0
                                                    bg-gradient-to-t
                                                    from-black/50
                                                    via-black/5
                                                    to-transparent
                                                    opacity-70
                                                    group-hover:opacity-90
                                                    transition-opacity
                                                    duration-300
                                                " />

                                                {/* =================================================
                                                    CONDITION BADGE
                                                ================================================= */}

                                                {item.condition && (
                                                    <span className="
                                                        absolute
                                                        left-2
                                                        sm:left-3
                                                        top-2
                                                        sm:top-3
                                                        max-w-[55%]
                                                        px-2
                                                        sm:px-2.5
                                                        py-1
                                                        rounded-lg
                                                        sm:rounded-xl
                                                        bg-black/60
                                                        backdrop-blur-md
                                                        border
                                                        border-white/10
                                                        text-white
                                                        text-[9px]
                                                        sm:text-[11px]
                                                        font-semibold
                                                        truncate
                                                        shadow-lg
                                                    ">
                                                        {item.condition}
                                                    </span>
                                                )}

                                                {/* =================================================
                                                    FAVORITE BUTTON
                                                ================================================= */}

                                                <button
                                                    type="button"
                                                    aria-label={
                                                        relatedIsFavorite
                                                            ? "Sevimlilərdən sil"
                                                            : "Sevimlilərə əlavə et"
                                                    }
                                                    title={
                                                        relatedIsFavorite
                                                            ? "Sevimlilərdən sil"
                                                            : "Sevimlilərə əlavə et"
                                                    }
                                                    onClick={(e) =>
                                                        handleRelatedFavorite(
                                                            e,
                                                            item
                                                        )
                                                    }
                                                    disabled={
                                                        relatedIsLoading
                                                    }
                                                    className={`
                                                        absolute
                                                        right-2
                                                        sm:right-3
                                                        top-2
                                                        sm:top-3
                                                        z-20
                                                        w-10
                                                        h-10
                                                        sm:w-11
                                                        sm:h-11
                                                        rounded-full
                                                        flex
                                                        items-center
                                                        justify-center
                                                        backdrop-blur-xl
                                                        border
                                                        shadow-xl
                                                        transition-all
                                                        duration-300
                                                        disabled:cursor-wait
                                                        disabled:opacity-70

                                                        ${relatedIsFavorite
                                                            ? "bg-red-500 border-red-300 text-white shadow-red-500/30 hover:bg-red-600 hover:scale-105"
                                                            : "bg-white/90 dark:bg-zinc-900/90 border-white/60 dark:border-zinc-700 text-gray-500 dark:text-gray-300 hover:bg-white hover:text-red-500 dark:hover:bg-zinc-800 dark:hover:text-red-400 hover:scale-105"
                                                        }
                                                    `}
                                                >

                                                    {relatedIsLoading ? (
                                                        <span className="
                                                            w-4
                                                            h-4
                                                            sm:w-5
                                                            sm:h-5
                                                            rounded-full
                                                            border-2
                                                            border-current
                                                            border-t-transparent
                                                            animate-spin
                                                        " />
                                                    ) : (
                                                        <FaHeart
                                                            className={`
                                                                text-base
                                                                sm:text-lg
                                                                transition-transform
                                                                duration-300
                                                                ${relatedIsFavorite
                                                                    ? "scale-110"
                                                                    : "group-hover:scale-110"
                                                                }
                                                            `}
                                                        />
                                                    )}

                                                </button>

                                                {/* =================================================
                                                    FAVORITE STATUS LABEL
                                                ================================================= */}

                                                {relatedIsFavorite && (
                                                    <div className="
                                                        absolute
                                                        left-2
                                                        sm:left-3
                                                        bottom-2
                                                        sm:bottom-3
                                                        z-10
                                                        inline-flex
                                                        items-center
                                                        gap-1
                                                        sm:gap-1.5
                                                        px-2
                                                        sm:px-2.5
                                                        py-1
                                                        rounded-full
                                                        bg-red-500/90
                                                        backdrop-blur-md
                                                        text-white
                                                        text-[8px]
                                                        sm:text-[10px]
                                                        font-bold
                                                        shadow-lg
                                                    ">
                                                        <FaHeart className="text-[8px] sm:text-[9px]" />

                                                        <span>
                                                            Sevimlidə
                                                        </span>
                                                    </div>
                                                )}

                                            </div>

                                            {/* =================================================
                                                CONTENT
                                            ================================================= */}

                                            <div className="p-2.5 sm:p-4">

                                                {/* TITLE */}

                                                <h3 className="
                                                    font-semibold
                                                    text-xs
                                                    sm:text-base
                                                    leading-4
                                                    sm:leading-5
                                                    text-gray-900
                                                    dark:text-white
                                                    line-clamp-2
                                                    min-h-[32px]
                                                    sm:min-h-[40px]
                                                    group-hover:text-blue-600
                                                    dark:group-hover:text-blue-400
                                                    transition-colors
                                                ">
                                                    {item.title ||
                                                        "Adsız elan"}
                                                </h3>

                                                {/* PRICE */}

                                                <div className="mt-2.5 sm:mt-3">

                                                    <span className="
                                                        block
                                                        text-sm
                                                        sm:text-xl
                                                        font-extrabold
                                                        text-gray-900
                                                        dark:text-white
                                                        truncate
                                                    ">

                                                        {Number(
                                                            item.price ||
                                                            0
                                                        ).toLocaleString(
                                                            "az-AZ"
                                                        )}{" "}

                                                        ₼

                                                    </span>

                                                </div>

                                                {/* LOCATION */}

                                                {item.location && (
                                                    <div className="
                                                        flex
                                                        items-center
                                                        gap-1
                                                        sm:gap-1.5
                                                        mt-2
                                                        sm:mt-2.5
                                                        text-[10px]
                                                        sm:text-sm
                                                        text-gray-500
                                                        dark:text-gray-400
                                                    ">

                                                        <FaMapMarkerAlt className="
                                                            shrink-0
                                                            text-[9px]
                                                            sm:text-xs
                                                            text-gray-400
                                                        " />

                                                        <span className="truncate">
                                                            {item.location}
                                                        </span>

                                                    </div>
                                                )}

                                                {/* =================================================
                                                    MOBILE ACTION HINT
                                                ================================================= */}

                                                <div className="
                                                    mt-3
                                                    sm:hidden
                                                    flex
                                                    items-center
                                                    justify-between
                                                    gap-2
                                                ">

                                                    <span className="
                                                        text-[9px]
                                                        font-semibold
                                                        text-gray-400
                                                        dark:text-gray-500
                                                    ">
                                                        Ətraflı bax
                                                    </span>

                                                    <div className="
                                                        w-7
                                                        h-7
                                                        rounded-full
                                                        bg-blue-50
                                                        dark:bg-blue-950/40
                                                        text-blue-600
                                                        dark:text-blue-400
                                                        flex
                                                        items-center
                                                        justify-center
                                                    ">
                                                        <FaChevronRight className="text-[9px]" />
                                                    </div>

                                                </div>

                                            </div>

                                        </article>
                                    );
                                }
                            )}

                        </div>

                    </section>
                )}

            </div>

        </div>
    );
};

export default DetalPaginat;